import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { setValidateLogin, validateLogin } from '@portal/actions/onboarding';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import { createAccountStatus } from '@portal/enum/createAccountError';

import { getRouteStackPath } from '@portal/config/routes';
import { validateStatus } from '@portal/enum/validateStatus';
import useReduxState from '@portal/hooks/useReduxState';
import { passwordRules } from '@portal/config/app.json';

import { translate } from '@portal/services/i18n';
import {
  checkPasswordValidation,
  validatePassword,
} from '@portal/utils/validate';
import { Col } from 'antd';

import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import * as MessageService from '~/services/message';
import PasswordCheck from '@portal/components/PasswordCheck/PasswordCheck';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import { useNavigation } from '@portal/hooks/useNavigation';

const CredentialMultiAccess = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation<{
    account: models.AccountMultiAccess;
    credential?: models.CredentialMultiAccess;
    userId: string;
  }>();

  const {
    onboarding: { loginStatus },
    user: { me },
  } = useReduxState();

  const [showPassword, setShowPassword] = useState(false);
  const [credential, setCredential] = useState<models.CredentialMultiAccess>({
    login: '',
    password: '',
  });

  useEffect(() => {
    if (!navigate?.location?.state?.account) {
      MessageService.error('MULTI_ACCESS.NO_ACCOUNT');
      navigate(getRouteStackPath('PROFILE', 'ACCOUNT_MULTI_ACCESS'));
    }

    if (navigate?.location?.state?.credential) {
      setCredential(navigate?.location?.state?.credential);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loginStatus?.status === validateStatus.OK && credential.password) {
      handleValidatePassword();
    } else if (
      loginStatus?.status === validateStatus.IN_USE ||
      loginStatus?.status === createAccountStatus.USER_ALREADY_CREATED
    ) {
      MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.USER_ALREADY_CREATED',
        { domain: 'validation' }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginStatus]);

  const handleValidatePassword = () => {
    if (checkPasswordValidation(validatePassword(credential.password))) {
      if (navigate?.location?.state?.account) {
        navigate(getRouteStackPath('PROFILE', 'PERMISSIONS_MULTI_ACCESS'), {
          state: {
            account: navigate.location.state.account,
            credential,
            userId: me.id ?? '',
          },
        });
      }
    } else {
      return MessageService.error(
        'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.PASSWORD',
        { domain: 'validation' }
      );
    }
    dispatch(setValidateLogin());
  };

  const handleValidateLogin = () => {
    if (!/^[a-zA-Z]{1}[_.\-a-zA-Z0-9 ]+$/.test(credential.login)) {
      MessageService.error('INVALID_LOGIN');

      return;
    }
    dispatch(validateLogin(credential.login));
  };

  return (
    <>
      <AdvancedHeader
        title={translate('APPLICATION.TITLE')}
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('PAGES.HOME.PAY.BREADCRUMB.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: true,
            title: translate('PAGES.MY_ACCOUNT.TABS.ACCOUNT'),
            url: getRouteStackPath('PROFILE', 'PROFILE_PAGE'),
          },
          {
            active: true,
            title: translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.TITLE'),
            url: getRouteStackPath('PROFILE', 'MULTI_ACCESS'),
          },
          {
            active: true,
            title: translate(
              'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.ACCOUNT.TITLE'
            ),
            url: getRouteStackPath('PROFILE', 'ACCOUNT_MULTI_ACCESS'),
            state: {
              account: navigate?.location?.state?.account,
            },
          },
          {
            active: false,
            title: translate(
              'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.CREDENTIALS.TITLE'
            ),
          },
        ]}
      />
      <div className="multiaccess-page">
        <AdvancedPanelContentHeader
          testId="create-account-credential-header"
          title={translate(
            'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.CREDENTIALS.TITLE'
          )}
          description={translate(
            'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.CREDENTIALS.DESCRIPTION'
          )}
          goBackAction={() => navigate.back()}
        />
        <AdvancedCard testId="create-account-credential-card">
          <Col className="create-account-credential__col">
            <AdvancedInput
              label={translate(
                'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.CREDENTIALS.LOGIN'
              )}
              value={credential.login}
              variant="outlined"
              onChange={(value) =>
                setCredential({ ...credential, login: value.replace(/ /g, '') })
              }
            />
          </Col>
          <Col className="create-account-credential__col">
            <AdvancedInput
              label={translate(
                'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.CREDENTIALS.PASSWORD'
              )}
              value={credential.password}
              variant="outlined"
              onChange={(value) =>
                setCredential({ ...credential, password: value })
              }
              placeholder={'*'.repeat(passwordRules.minLength ?? 8)}
              securedTextField={!showPassword}
              endIcon={
                !showPassword ? (
                  <EyeOutlined onClick={() => setShowPassword(true)} />
                ) : (
                  <EyeInvisibleOutlined
                    onClick={() => setShowPassword(false)}
                  />
                )
              }
              maxLength={passwordRules.maxLength}
            />
            <div className="mt-4">
              <PasswordCheck
                password={credential.password}
                passwordValidation={validatePassword(credential.password)}
              />
            </div>
          </Col>
          <div className="multiaccess-page__buttons">
            <AdvancedButton
              size="large"
              text={translate('PAGES.MY_ACCOUNT.MULTI_ACCESS.BUTTON')}
              disabled={
                !credential.login ||
                !credential.password ||
                !checkPasswordValidation(validatePassword(credential.password))
              }
              onClick={handleValidateLogin}
            />
          </div>
        </AdvancedCard>
      </div>
    </>
  );
};

export default CredentialMultiAccess;
